import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  ExposureDirectionType,
  FootprintPolygonsType,
  GridSpaceDataType,
  LevelType,
  PositionType,
  SpaceType
} from 'data/types';
import {
  openingRunsWithOpenNeighborOverhang,
  openingRunWithOpenNeighborOverhang
} from 'utils/open-neighbor-opening-runs';
import { defaultOpeningWidthFt } from 'utils/opening-drag';
import { getSpaceEdgeRuns } from 'utils/opening-layout';
import {
  openingSpanOnRun,
  resolveOpeningOffsets
} from 'utils/opening-position';
import { openingLayoutsFromEdgeItems } from 'utils/opening-run-layout';
import { OpeningRunLayoutType } from 'utils/opening-run-layout-types';
import { getPolygonEdgeOpeningRuns } from 'utils/polygon-edge-opening-runs';
import {
  PtacWallOpening,
  PtacWallOpenings,
  PtacWallOpeningsLookup,
  RoomRunContextType
} from 'utils/ptac-wall-openings-types';
import {
  spaceOpenNeighborRuns,
  spaceOriginFeet
} from 'utils/space-opening-run';
import { isPolygonGridSpace } from 'utils/space-polygon';
import {
  findLevelBySpaceId,
  findSpaceById,
  getEdgeItemsFromSpace
} from 'utils/structure';

function roomRunContext(
  room: GridSpaceDataType,
  space: SpaceType | null,
  structure: LevelType[],
  homeOrientation: ExposureDirectionType | undefined
): RoomRunContextType {
  if (!space) {
    return {
      originFeet: {
        x: room.position.x / STRUCTURE_GRID_SIZE,
        y: room.position.y / STRUCTURE_GRID_SIZE
      },
      neighbors: []
    };
  }
  return {
    originFeet: spaceOriginFeet(space),
    neighbors: spaceOpenNeighborRuns(
      space,
      findLevelBySpaceId(structure, room.spaceId),
      homeOrientation
    )
  };
}

function polygonLayouts(
  room: GridSpaceDataType,
  sizingAttributes: NonNullable<SpaceType['sizingAttributes']>,
  footprints: FootprintPolygonsType | null,
  homeOrientation: ExposureDirectionType | undefined,
  context: RoomRunContextType
): OpeningRunLayoutType[] {
  const { vertices } = room;
  if (
    !vertices ||
    !isPolygonGridSpace(vertices, room.position.width, room.position.height)
  ) {
    return [];
  }
  return getPolygonEdgeOpeningRuns({
    originFeet: context.originFeet,
    verticesFeet: vertices.map((vertex) => ({
      x: vertex.x / STRUCTURE_GRID_SIZE,
      y: vertex.y / STRUCTURE_GRID_SIZE
    })),
    footprints,
    walls: [
      ...sizingAttributes.aboveGradeWalls,
      ...sizingAttributes.belowGradeWalls
    ],
    homeOrientation
  }).map((layout) => ({
    ...layout,
    run: openingRunWithOpenNeighborOverhang(
      layout.run,
      context.originFeet,
      context.neighbors
    )
  }));
}

function roomOpeningLayouts(
  room: GridSpaceDataType,
  structure: LevelType[],
  footprints: FootprintPolygonsType | null,
  homeOrientation: ExposureDirectionType | undefined
): { layouts: OpeningRunLayoutType[]; originFeet: PositionType } {
  const space = findSpaceById(structure, room.spaceId);
  const context = roomRunContext(room, space, structure, homeOrientation);
  const sizingAttributes = space?.sizingAttributes;
  if (!sizingAttributes) {
    return { layouts: [], originFeet: context.originFeet };
  }
  const polygon = polygonLayouts(
    room,
    sizingAttributes,
    footprints,
    homeOrientation,
    context
  );
  if (polygon.length > 0) {
    return { layouts: polygon, originFeet: context.originFeet };
  }
  return {
    layouts: openingLayoutsFromEdgeItems(
      getEdgeItemsFromSpace(structure, room.spaceId, homeOrientation),
      openingRunsWithOpenNeighborOverhang(
        getSpaceEdgeRuns(room, footprints),
        context.originFeet,
        context.neighbors
      )
    ),
    originFeet: context.originFeet
  };
}

function layoutOpenings(
  layout: OpeningRunLayoutType,
  origin: PositionType
): PtacWallOpening[] {
  const { run, items } = layout;
  const sized = items.map((item) => ({
    id: item.id,
    widthFt: item.width ?? defaultOpeningWidthFt(item.type),
    positionOnWall: item.positionOnWall
  }));
  const offsets = resolveOpeningOffsets(sized, run.lengthFt);
  return sized.map((item, index) => {
    const span = openingSpanOnRun(
      run,
      offsets[index]?.offsetFt ?? 0,
      item.widthFt
    );
    return {
      start: { x: origin.x + span.p1.x, y: origin.y + span.p1.y },
      end: { x: origin.x + span.p2.x, y: origin.y + span.p2.y }
    };
  });
}

export function ptacWallOpeningsForRoom(
  room: GridSpaceDataType,
  structure: LevelType[],
  footprints: FootprintPolygonsType | null,
  homeOrientation?: ExposureDirectionType
): PtacWallOpenings {
  const { layouts, originFeet } = roomOpeningLayouts(
    room,
    structure,
    footprints,
    homeOrientation
  );
  return layouts.flatMap((layout) => layoutOpenings(layout, originFeet));
}

export function ptacWallOpeningsLookup(
  structure: LevelType[],
  footprints: FootprintPolygonsType | null,
  homeOrientation?: ExposureDirectionType
): PtacWallOpeningsLookup {
  const cache = new Map<string, PtacWallOpenings>();
  return (room) => {
    const cached = cache.get(room.spaceId);
    if (cached) return cached;
    const openings = ptacWallOpeningsForRoom(
      room,
      structure,
      footprints,
      homeOrientation
    );
    cache.set(room.spaceId, openings);
    return openings;
  };
}
